// built-from: /value-stream-mapping-workspace v1 impl:value-stream-mapping-workspace role:implementation
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Gauge,
  ListChecks,
  Snowflake,
} from 'lucide-react';
import { useEngagement } from '../hooks/useEngagement';
import { useSharedStream } from '../hooks/useSharedStream';
import {
  createCurrentStateVsmBaselineSnapshot,
  deriveCurrentStateVsmReadModel,
  type CurrentStateVsmBottleneck,
  type CurrentStateVsmFlowStep,
  type CurrentStateVsmMetrics,
  type CurrentStateVsmMissingFact,
} from '../utils/currentStateVsmReadModel';
import type {
  StreamWorkspaceReadPanelProps,
  StreamWorkspaceStream,
} from './streamMappingWorkspaceTypes';
import { EMPTY_STREAM_READ_SESSION } from './streamMappingWorkspaceTypes';
import '../styles/StreamCurrentStateVsmPanel.css';

const INITIAL_MISSING_FACT_LIMIT = 5;

const StreamCurrentStateVsmPanel: React.FC<StreamWorkspaceReadPanelProps> = ({
  stream,
  session = EMPTY_STREAM_READ_SESSION,
  readOnly = false,
}) => {
  const { snapshot, saveStreams, currentEngagementId } = useEngagement();
  const linked = Boolean(stream.sharedProcesses) && !readOnly;
  const shared = useSharedStream(currentEngagementId, linked ? stream.id : null);
  const [measuring, setMeasuring] = useState<string | null>(null);
  const [values, setValues] = useState({ ct: '', wt: '', ca: '', notes: '' });
  const [savingBaseline, setSavingBaseline] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);

  const readModel = useMemo(
    () =>
      deriveCurrentStateVsmReadModel({
        stream,
        sessionId: session.sessionId,
        flowText: session.newModel.flowText,
        vsm: session.newModel.vsm,
        spine: session.spine,
        ...(linked ? { shared: shared.shared || { processIds: [], steps: [] } } : {}),
      }),
    [session.newModel.flowText, session.newModel.vsm, session.sessionId, session.spine, stream, linked, shared.shared],
  );

  const freezeBaseline = async () => {
    if (!readModel.canFreezeBaseline) return;
    setSavingBaseline(true);
    setSaveError(null);

    try {
      const streams = streamsFromSnapshot(snapshot);
      const baseline = createCurrentStateVsmBaselineSnapshot(readModel);
      let matched = false;
      const next = streams.map((candidate) => {
        if (candidate.id !== stream.id) return candidate;
        matched = true;
        return {
          ...candidate,
          vsmBaseline: baseline,
        };
      });

      if (!matched) {
        next.push({
          ...stream,
          vsmBaseline: baseline,
        });
      }

      await saveStreams({ streams: next });
      setConfirmReplaceOpen(false);
    } catch (err) {
      setSaveError((err as Error).message || 'Could not freeze the baseline.');
    } finally {
      setSavingBaseline(false);
    }
  };

  const onFreezeClick = () => {
    if (readModel.hasBaseline) {
      setConfirmReplaceOpen(true);
      return;
    }
    void freezeBaseline();
  };

  return (
    <section className="smw-leaf csvsm-panel" data-testid="stream-current-state-vsm-panel">
      <div className="smw-panel-head csvsm-head">
        <div>
          <h2>Current state VSM</h2>
          <p>Read the flow, time, waits, quality, and open questions in workshop language.</p>
        </div>
        <div className="csvsm-head-actions">
          {readModel.hasBaseline ? (
            <span className="csvsm-baseline-pill">
              <CheckCircle2 size={15} aria-hidden="true" />
              Frozen
            </span>
          ) : null}
          {!readOnly ? (
            <button
              type="button"
              className="csvsm-freeze"
              onClick={onFreezeClick}
              disabled={!readModel.canFreezeBaseline || savingBaseline}
            >
              <Snowflake size={15} aria-hidden="true" />
              {readModel.hasBaseline ? 'Replace baseline' : 'Freeze baseline'}
            </button>
          ) : null}
        </div>
      </div>

      {saveError || shared.error ? (
        <div className="csvsm-alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          {saveError || shared.error}
        </div>
      ) : null}
      {linked ? <section aria-label="Shared step measurements">
        <p>Processes: {shared.shared?.processes?.map(p => p.name).join(' · ') || 'Loading…'}</p>
        {shared.shared?.steps.map(step => <button key={step.stepId} onClick={() => { setMeasuring(step.stepId); setValues({ ct: String(step.measurement?.ct ?? ''), wt: String(step.measurement?.wt ?? ''), ca: String(step.measurement?.ca ?? ''), notes: String(step.measurement?.notes ?? '') }); }}>Measure {step.label}</button>)}
        {shared.shared?.unresolvedReferences.map(ref => <p role="alert" key={ref.stepId}>{ref.stepId}: {ref.reason}. Retained values: {JSON.stringify(ref.values)}</p>)}
        {measuring ? <form onSubmit={e => { e.preventDefault(); const recorded = shared.shared?.steps.find(step => step.stepId === measuring)?.measurement || {}; void shared.measure(measuring, { ...recorded, ...values, ca: values.ca === '' ? null : Number(values.ca) }).then(ok => { if (ok) setMeasuring(null); }); }}>
          <p>Measurements stay attached to this step, including after a rename or reorder.</p>
          <label>Work time<input placeholder="e.g. 2m" value={values.ct} onChange={e => setValues({ ...values, ct: e.target.value })} /></label>
          <label>Wait time<input placeholder="e.g. 1h" value={values.wt} onChange={e => setValues({ ...values, wt: e.target.value })} /></label>
          <label>Complete and accurate (%)<input type="number" min="0" max="100" value={values.ca} onChange={e => setValues({ ...values, ca: e.target.value })} /></label>
          <label>Notes and issues<textarea value={values.notes} onChange={e => setValues({ ...values, notes: e.target.value })} /></label>
          <button disabled={shared.saving}>Save measurement</button><button type="button" onClick={() => setMeasuring(null)}>Cancel</button>
        </form> : null}
      </section> : null}

      {readModel.hasBaseline ? (
        <div className="csvsm-baseline-state">
          <CheckCircle2 size={16} aria-hidden="true" />
          Baseline frozen
          {readModel.baselineSessionId ? ` from session ${readModel.baselineSessionId}` : ''}
        </div>
      ) : null}
      {linked && readModel.hasBaseline ? <details><summary>Original frozen baseline</summary>
        <p>The baseline below is the recorded snapshot. Live shared steps and measures are shown separately; later process changes do not rewrite this evidence.</p>
        <pre>{JSON.stringify(stream.vsmBaseline, null, 2)}</pre>
        {JSON.stringify((stream.vsmBaseline as { currentStateVsm?: { flow?: unknown } })?.currentStateVsm?.flow) !== JSON.stringify(readModel.flow) ? <p role="status">The working process or its measures differ from the frozen baseline. Review before replacing it.</p> : null}
      </details> : null}

      {readModel.status === 'empty' ? (
        <div className="csvsm-empty" role="status">
          <ListChecks size={18} aria-hidden="true" />
          <div>
            <h3>No current-state flow yet</h3>
            <p>Capture the stream story in chat and the readout will form here.</p>
          </div>
        </div>
      ) : (
        <>
          <MetricsBand metrics={readModel.metrics} />
          <FlowReadout flow={readModel.flow} />
          <div className="csvsm-two-column">
            <Bottlenecks bottlenecks={readModel.bottlenecks} />
            <MissingFacts missingFacts={readModel.missingFacts} />
          </div>
        </>
      )}

      {!readOnly && confirmReplaceOpen ? (
        <div className="csvsm-dialog-backdrop">
          <div className="csvsm-dialog" role="alertdialog" aria-label="Replace baseline">
            <h2>Replace baseline</h2>
            <p>The frozen baseline stays in place until the replacement saves.</p>
            <div className="csvsm-dialog-actions">
              <button
                type="button"
                className="csvsm-freeze"
                onClick={() => void freezeBaseline()}
                disabled={savingBaseline}
              >
                Confirm replace baseline
              </button>
              <button
                type="button"
                className="csvsm-secondary"
                onClick={() => setConfirmReplaceOpen(false)}
                disabled={savingBaseline}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const MetricsBand: React.FC<{ metrics: CurrentStateVsmMetrics }> = ({ metrics }) => (
  <section className="csvsm-metrics" aria-label="Current-state measures">
    <MetricTile icon={<Clock3 size={16} aria-hidden="true" />} label="Lead time" value={metrics.leadTime.display} />
    <MetricTile label="Work time" value={metrics.workTime.display} />
    <MetricTile label="Wait time" value={metrics.waitTime.display} />
    <MetricTile
      icon={<Gauge size={16} aria-hidden="true" />}
      label="Flow efficiency"
      value={metrics.flowEfficiency.display}
    />
    <MetricTile label="First time right" value={metrics.quality.display} />
  </section>
);

const MetricTile: React.FC<{
  icon?: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className={`csvsm-metric${value === 'Missing' ? ' csvsm-metric--missing' : ''}`}>
    <span>
      {icon}
      {label}
    </span>
    <strong>{value}</strong>
  </div>
);

const FlowReadout: React.FC<{ flow: CurrentStateVsmFlowStep[] }> = ({ flow }) => (
  <section className="csvsm-section" aria-label="Current-state flow">
    <div className="csvsm-section-head">
      <h3>Ordered flow</h3>
      <span>{flow.length} step{flow.length === 1 ? '' : 's'}</span>
    </div>
    <ol className="csvsm-flow">
      {flow.map((step) => {
        const hasWait = step.waitTime.status === 'known' && step.waitTime.seconds > 0;
        return (
          <li
            key={step.id}
            className={`csvsm-flow-step${hasWait ? ' csvsm-flow-step--wait' : ''}`}
            data-wait={step.waitTime.display}
          >
            <span className="csvsm-step-number">{step.order}</span>
            <div className="csvsm-step-body">
              <h4>{step.label}</h4>
              <dl>
                <div>
                  <dt>Work</dt>
                  <dd>{step.workTime.display}</dd>
                </div>
                <div>
                  <dt>Wait</dt>
                  <dd>{step.waitTime.display}</dd>
                </div>
                <div>
                  <dt>Quality</dt>
                  <dd>{step.quality.display}</dd>
                </div>
                <div>
                  <dt>Why it waits</dt>
                  <dd>{step.waitReason ?? (hasWait ? 'Missing' : 'No wait found')}</dd>
                </div>
              </dl>
            </div>
          </li>
        );
      })}
    </ol>
  </section>
);

const Bottlenecks: React.FC<{ bottlenecks: CurrentStateVsmBottleneck[] }> = ({
  bottlenecks,
}) => (
  <section className="csvsm-section">
    <div className="csvsm-section-head">
      <h3>Likely bottlenecks</h3>
      <span>{bottlenecks.length}</span>
    </div>
    {bottlenecks.length > 0 ? (
      <ul className="csvsm-list">
        {bottlenecks.map((bottleneck) => (
          <li key={`${bottleneck.kind}-${bottleneck.stepId ?? bottleneck.stepLabel}`}>
            <strong>{bottleneck.stepLabel}</strong>
            <span>{bottleneck.reason}</span>
            <small>{bottleneck.detail}</small>
          </li>
        ))}
      </ul>
    ) : (
      <p className="csvsm-muted">No bottleneck candidate is clear yet.</p>
    )}
  </section>
);

const MissingFacts: React.FC<{ missingFacts: CurrentStateVsmMissingFact[] }> = ({
  missingFacts,
}) => {
  const [expanded, setExpanded] = useState(false);
  const visibleFacts = expanded
    ? missingFacts
    : missingFacts.slice(0, INITIAL_MISSING_FACT_LIMIT);
  const hiddenCount = Math.max(0, missingFacts.length - visibleFacts.length);

  return (
    <section className="csvsm-section">
      <div className="csvsm-section-head">
        <h3>Missing facts</h3>
        <span>{missingFacts.length}</span>
      </div>
      {missingFacts.length > 0 ? (
        <>
          <p className="csvsm-muted">
            Capture the headline timings first. The detailed checklist can open when the room is ready.
          </p>
          <ul className="csvsm-list csvsm-list--missing">
            {visibleFacts.map((fact, index) => (
              <li key={`${fact.kind}-${fact.stepId ?? index}`}>
                <AlertTriangle size={15} aria-hidden="true" />
                <span>{fact.message}</span>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 || expanded ? (
            <button
              type="button"
              className="csvsm-missing-toggle"
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded
                ? 'Show fewer missing facts'
                : `Show all ${missingFacts.length} missing facts`}
            </button>
          ) : null}
        </>
      ) : (
        <p className="csvsm-muted">The timing and quality facts are ready to discuss.</p>
      )}
    </section>
  );
};

function streamsFromSnapshot(snapshot: unknown): StreamWorkspaceStream[] {
  const streamsDoc = (snapshot as { streams?: { streams?: unknown } } | null)?.streams;
  return Array.isArray(streamsDoc?.streams) ? (streamsDoc.streams as StreamWorkspaceStream[]) : [];
}

export default StreamCurrentStateVsmPanel;
